import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The actions cell of a config row (AUT-131). Two modes:
 *   - editing  → Save / Cancel. The save is role + permission + audit gated
 *                server-side.
 *   - display  → history toggle (always) + Edit (only when `editable`;
 *                architect guardrail #2 — absent keys are read-only).
 *
 * Extracted from `ConfigRow` to keep its render under the function-line cap.
 */
export interface ConfigRowActionsProps {
  editing: boolean;
  editable: boolean;
  busy: boolean;
  historyOpen: boolean;
  onToggleHistory: () => void;
  onBeginEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}

export const ConfigRowActions = ({
  editing,
  editable,
  busy,
  historyOpen,
  onToggleHistory,
  onBeginEdit,
  onSave,
  onCancel,
}: ConfigRowActionsProps): ReactElement => {
  const { t } = useTranslation();

  if (editing) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Button
          size="small"
          variant="contained"
          disabled={busy}
          onClick={onSave}
          data-testid="config-save-button"
        >
          {t('common.save')}
        </Button>
        <Button size="small" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={t('config.actions.history')}>
        <IconButton size="small" aria-label={t('config.actions.history')} onClick={onToggleHistory}>
          {historyOpen ? <ExpandLessIcon fontSize="small" /> : <HistoryIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      {editable ? (
        <Button
          size="small"
          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
          onClick={onBeginEdit}
          data-testid="config-edit-button"
          disabled={busy}
        >
          {t('config.actions.edit')}
        </Button>
      ) : null}
    </Box>
  );
};
